import React from "react";
import PropTypes from "prop-types";
import { 
    NeoCard, 
    NeoButton, 
    NeoInput, 
    NeoTable, 
    NeoEmptyState, 
    AvatarPreview, 
    StatusBadge,
    NeoLoader,
    NeoBadge
} from "../../../../components/Common";
import { GLOBAL_STATUS, GLOBAL_STATUS_LABELS } from "../../../../constants/common";
import { MaskHelper } from "../../../../utils/helpers";

/**
 * PortfolioDetail
 * Painel direito do Master-Detail de Carteira de Clientes.
 * Exibe os clientes do colaborador selecionado e permite a seleção múltipla e transferência.
 * Migrado 100% para o Neo Design System com zero estilos inline.
 */
const PortfolioDetail = ({
    employee,
    members,
    selectedMemberIds,
    onToggleMember,
    onToggleSelectAll,
    onTransferClick,
    loadingMembers,
    searchTerm,
    setSearchTerm,
    statusFilter,
    setStatusFilter,
    filteredMembers
}) => {
    const allSelected = filteredMembers.length > 0 &&
        filteredMembers.every(m => selectedMemberIds.includes(m.id));

    const employeeName = employee?.fullName || `${employee?.firstName || ""} ${employee?.lastName || ""}`.trim() || "Colaborador";

    // Estado vazio: nenhum employee selecionado
    if (!employee) {
        return (
            <NeoEmptyState
                icon="mdi-folder-account-outline"
                title="Selecione um colaborador"
                description="Escolha um colaborador na lista ao lado para visualizar e gerenciar sua carteira de clientes atribuídos."
            />
        );
    }

    return (
        <NeoCard
            title={employeeName}
            subtitle={
                <span className="d-flex align-items-center gap-2 mt-1">
                    <span>{employee.roleName || employee.role || "Colaborador"} — Carteira de Clientes</span>
                    <NeoBadge color={employee.status === GLOBAL_STATUS.ACTIVE ? "success" : "danger"}>
                        {GLOBAL_STATUS_LABELS[employee.status] || "Desconhecido"}
                    </NeoBadge>
                </span>
            }
            headerIcon="mdi-account-tie"
            headerActions={
                <NeoButton
                    variant="warning"
                    disabled={selectedMemberIds.length === 0}
                    onClick={onTransferClick}
                    icon="mdi-account-switch-outline"
                    size="sm"
                >
                    Transferir ({selectedMemberIds.length})
                </NeoButton>
            }
        >
            {/* Loading */}
            {loadingMembers ? (
                <NeoLoader type="card" message="Carregando clientes..." />
            ) : members.length === 0 ? (
                /* Sem membros */
                <NeoEmptyState
                    icon="mdi-account-off-outline"
                    title="Nenhum cliente nesta carteira"
                    description="Este colaborador não possui clientes atribuídos como consultor responsável."
                />
            ) : (
                <>
                    {/* Toolbar de Filtros */}
                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
                        <span className="text-muted font-size-13 fw-semibold">
                            <strong>{filteredMembers.length}</strong> cliente(s) na carteira
                            {selectedMemberIds.length > 0 && (
                                <NeoBadge color="accent" className="ms-2">
                                    {selectedMemberIds.length} selecionado(s)
                                </NeoBadge>
                            )}
                        </span>

                        <div className="d-flex gap-2 flex-shrink-0 align-items-center">
                            {/* Filtro de Status */}
                            <div className="d-flex align-items-center">
                                <NeoInput
                                    type="select"
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                    options={[
                                        { value: "all", label: "Todos Status" },
                                        { value: "active", label: "Ativos" },
                                        { value: "inactive", label: "Inativos" },
                                        { value: "suspended", label: "Suspensos" },
                                        { value: "lead", label: "Leads" },
                                        { value: "cancelled", label: "Cancelados" }
                                    ]}
                                    size="sm"
                                    icon="mdi-filter-variant"
                                />
                            </div>

                            {/* Busca por nome */}
                            <div className="search-box-premium">
                                <NeoInput
                                    type="text"
                                    placeholder="Buscar cliente..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    icon="mdi-magnify"
                                    size="sm"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Tabela com NeoTable */}
                    <NeoTable
                        columns={[
                            {
                                key: "select",
                                width: "50px",
                                label: (
                                    <div className="form-check m-0 p-0 d-flex align-items-center justify-content-center">
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={onToggleSelectAll}
                                            className="form-check-input m-0 cursor-pointer"
                                        />
                                    </div>
                                ),
                                render: (row) => (
                                    <div className="form-check m-0 p-0 d-flex align-items-center justify-content-center" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            checked={selectedMemberIds.includes(row.id)}
                                            onChange={() => onToggleMember(row.id)}
                                            className="form-check-input m-0 cursor-pointer"
                                        />
                                    </div>
                                )
                            },
                            {
                                key: "fullName",
                                label: "Cliente",
                                render: (row) => (
                                    <div
                                        className="d-flex align-items-center gap-2 cursor-pointer"
                                        onClick={() => onToggleMember(row.id)}
                                    >
                                        <AvatarPreview
                                            src={row.photoUrl}
                                            alt={row.fullName}
                                            size="xs"
                                            className="rounded-circle shadow-sm"
                                            fallbackType="initials"
                                        />
                                        <span className="fw-semibold text-dark text-truncate neo-truncate-180">
                                            {row.fullName || "Sem nome"}
                                        </span>
                                    </div>
                                )
                            },
                            {
                                key: "phone",
                                label: "Telefone",
                                render: (row) => <span className="text-muted">{MaskHelper.phone(row.phone) || "—"}</span>
                            },
                            {
                                key: "email",
                                label: "Email",
                                render: (row) => <span className="text-muted text-truncate neo-truncate-180">{row.email || "—"}</span>
                            },
                            {
                                key: "status",
                                label: "Status",
                                align: "center",
                                render: (row) => <StatusBadge status={row.status} />
                            }
                        ]}
                        data={filteredMembers}
                        defaultItemsPerPage={10}
                        emptyMessage="Nenhum cliente encontrado nessa carteira."
                    />
                </>
            )}
        </NeoCard>
    );
};

PortfolioDetail.propTypes = {
    employee: PropTypes.object,
    members: PropTypes.array.isRequired,
    selectedMemberIds: PropTypes.array.isRequired,
    onToggleMember: PropTypes.func.isRequired,
    onToggleSelectAll: PropTypes.func.isRequired,
    onTransferClick: PropTypes.func.isRequired,
    loadingMembers: PropTypes.bool,
    searchTerm: PropTypes.string,
    setSearchTerm: PropTypes.func,
    statusFilter: PropTypes.string,
    setStatusFilter: PropTypes.func,
    filteredMembers: PropTypes.array
};

export default PortfolioDetail;
